import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import './Home.css';
import { API_URL } from '../utils/config';

// Rotating hero words
const HERO_WORDS = ['WEAR', 'FEEL', 'OWN', 'BE'];

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wordIndex, setWordIndex] = useState(0);
  const [wordVisible, setWordVisible] = useState(true);
  const [addedId, setAddedId] = useState(null);
  const { addToCart } = useCart();

  // Rotating verb animation
  useEffect(() => {
    const interval = setInterval(() => {
      setWordVisible(false);
      setTimeout(() => {
        setWordIndex(i => (i + 1) % HERO_WORDS.length);
        setWordVisible(true);
      }, 350);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/products`);
      const productsData = Array.isArray(response.data)
        ? response.data
        : response.data.products || [];
      setProducts(productsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (imagePath && imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    setAddedId(product._id);
    setTimeout(() => setAddedId(null), 1500);
  };

  const featuredProducts = products.slice(0, 8);
  const newArrivals = products.slice(8, 16);

  if (loading) {
    return (
      <div className="myr-loading">
        <div className="myr-spinner" />
        <p>Loading collection…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="myr-error">
        <p>{error}</p>
        <button onClick={fetchProducts} className="myr-retry">Try again</button>
      </div>
    );
  }

  return (
    <div className="myr-home">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="myr-hero">
        <div className="myr-hero__left">
          <p className="myr-hero__eyebrow">New Collection · 2026</p>
          <h1 className="myr-hero__headline">
            <span className="myr-hero__verb-wrap">
              <span className={`myr-hero__verb${wordVisible ? ' visible' : ''}`}>
                {HERO_WORDS[wordIndex]}
              </span>
            </span>
            <br />
            <span className="myr-hero__sub-headline">YOUR STYLE</span>
          </h1>
          <p className="myr-hero__body">
            Curated fashion for the modern South Asian wardrobe.
            Up to 70% off this season.
          </p>
          <div className="myr-hero__actions">
            <Link to="/shop" className="myr-btn myr-btn--primary">Shop Now</Link>
            <Link to="/design" className="myr-btn myr-btn--ghost">Design your Won Item</Link>
          </div>
          <div className="myr-hero__stats">
            <div className="myr-hero__stat">
              <span className="myr-hero__stat-num">2K+</span>
              <span className="myr-hero__stat-lbl">Styles</span>
            </div>
            <div className="myr-hero__stat-div" />
            <div className="myr-hero__stat">
              <span className="myr-hero__stat-num">50K+</span>
              <span className="myr-hero__stat-lbl">Customers</span>
            </div>
            <div className="myr-hero__stat-div" />
            <div className="myr-hero__stat">
              <span className="myr-hero__stat-num">70%</span>
              <span className="myr-hero__stat-lbl">Off Sale</span>
            </div>
          </div>
        </div>
        <div className="myr-hero__right">
          <div className="myr-hero__img-frame">
            <img src="/img/hero/model2.png" alt="Moyurakkhi Collection" className="myr-hero__img"    
              onError={e => { e.target.style.display = 'none'; }} />
             {/* <div className="myr-hero__img-placeholder" aria-hidden="true" />  */}
            <div className="myr-hero__badge">
              <span className="myr-hero__badge-num">—70%</span>
              <span className="myr-hero__badge-txt">Sale On Now</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE STRIP ────────────────────────────────── */}
      <section className="myr-features">
        {[
          { icon: '🚚', label: 'Free Shipping', sub: 'On all orders' },
          { icon: '💳', label: 'Online Order',  sub: 'Secure checkout' },
          { icon: '💰', label: 'Save Money',    sub: 'Best prices' },
          { icon: '🎁', label: 'Promotions',    sub: 'Weekly deals' },
          { icon: '😊', label: 'Happy Sell',    sub: '50K+ served' },
          { icon: '🎧', label: '24/7 Support',  sub: 'Always here' },
        ].map(f => (
          <div className="myr-feature" key={f.label}>
            <span className="myr-feature__icon">{f.icon}</span>
            <div>
              <p className="myr-feature__label">{f.label}</p>
              <p className="myr-feature__sub">{f.sub}</p>
            </div>
          </div>
        ))}
      </section>

      {/* ── SECTION LABEL helper ─────────────────────────── */}

      {/* ── FEATURED PRODUCTS ────────────────────────────── */}
      <section className="myr-section myr-section--ivory">
        <div className="myr-section__head">
          <p className="myr-section__eyebrow">Handpicked for you</p>
          <h2 className="myr-section__title">Featured Products</h2>
          <p className="myr-section__desc">Summer Collection · New Modern Design</p>
        </div>
        <div className="myr-products">
          {featuredProducts.length > 0 ? (
            featuredProducts.map((product, i) => (
              <ProductCard
                key={product._id}
                product={product}
                getImageUrl={getImageUrl}
                onAddToCart={handleAddToCart}
                justAdded={addedId === product._id}
                accent={i % 4 === 0}
              />
            ))
          ) : (
            <p className="myr-empty">No products available yet.</p>
          )}
        </div>
        <div className="myr-section__foot">
          <Link to="/shop" className="myr-btn myr-btn--outline">View All Products</Link>
        </div>
      </section>

      {/* ── EDITORIAL BANNER ─────────────────────────────── */}
      <section className="myr-editorial-banner"
        style={{ backgroundImage: "url('/img/banner/b1.png')" }}>
        <div className="myr-editorial-banner__overlay" />
        <div className="myr-editorial-banner__content">
          <p className="myr-editorial-banner__eyebrow">Repair &amp; Care Services</p>
          <h2 className="myr-editorial-banner__headline">
            Up to <em>70% Off</em><br />All Customized Products
          </h2>
          <Link to="/shop" className="myr-btn myr-btn--teal">Explore More</Link>
        </div>
      </section>

      {/* ── NEW ARRIVALS ─────────────────────────────────── */}
      {newArrivals.length > 0 && (
        <section className="myr-section myr-section--white">
          <div className="myr-section__head">
            <p className="myr-section__eyebrow">Just dropped</p>
            <h2 className="myr-section__title">New Arrivals</h2>
            <p className="myr-section__desc">Latest Smart Home Collection</p>
          </div>
          <div className="myr-products">
            {newArrivals.map((product, i) => (
              <ProductCard
                key={product._id}
                product={product}
                getImageUrl={getImageUrl}
                onAddToCart={handleAddToCart}
                justAdded={addedId === product._id}
                accent={i % 5 === 2}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── DUAL BANNERS ─────────────────────────────────── */}
      <section className="myr-dual-banners">
        <div className="myr-dual-banner myr-dual-banner--a">
          <p className="myr-dual-banner__kicker">Crazy Deals</p>
          <h3 className="myr-dual-banner__title">Buy 1 Get 1 Free</h3>
          <p className="myr-dual-banner__body">
            The best smart home products on sale at Moyurakkhi
          </p>
          <Link to="/shop" className="myr-btn myr-btn--white-outline">Learn More</Link>
        </div>
        <div className="myr-dual-banner myr-dual-banner--b">
          <p className="myr-dual-banner__kicker">Spring / Summer</p>
          <h3 className="myr-dual-banner__title">Upcoming Season</h3>
          <p className="myr-dual-banner__body">
            Fresh arrivals every week — be the first to own
          </p>
          <Link to="/shop" className="myr-btn myr-btn--white-outline">View Collection</Link>
        </div>
      </section>

      {/* ── CATEGORY TILES ───────────────────────────────── */}
      <section className="myr-categories">
        <div className="myr-cat myr-cat--a">
          <h2 className="myr-cat__title">Seasonal Sale</h2>
          <p className="myr-cat__sub">Winter Collection · 50% Off</p>
        </div>
        <div className="myr-cat myr-cat--b">
          <h2 className="myr-cat__title">New Footwear</h2>
          <p className="myr-cat__sub">Spring / Summer 2025</p>
        </div>
        <div className="myr-cat myr-cat--c">
          <h2 className="myr-cat__title">T-Shirts</h2>
          <p className="myr-cat__sub">New Trendy Prints</p>
        </div>
      </section>

      {/* ── NEWSLETTER ───────────────────────────────────── */}
      <section className="myr-newsletter">
        <div className="myr-newsletter__inner">
          <p className="myr-newsletter__eyebrow">Stay in the loop</p>
          <h2 className="myr-newsletter__title">Sign Up for Newsletters</h2>
          <p className="myr-newsletter__desc">
            Get e-mail updates about our latest drops and <span>special offers.</span>
          </p>
          <div className="myr-newsletter__form">
            <input type="email" placeholder="Your email address" className="myr-newsletter__input" />
            <button className="myr-btn myr-btn--primary">Subscribe</button>
          </div>
        </div>
      </section>

    </div>
  );
};

/* ── Product Card sub-component ──────────────────────── */
function ProductCard({ product, getImageUrl, onAddToCart, justAdded, accent }) {
  return (
    <div className={`myr-card${accent ? ' myr-card--accent' : ''}`}>
      <Link to={`/product/${product._id}`} className="myr-card__img-wrap">
        <img
          src={getImageUrl(product.image)}
          alt={product.name}
          className="myr-card__img"
          onError={e => { e.target.src = '/img/products/f1.jpg'; }}
        />
        <span className="myr-card__overlay-label">View Product</span>
      </Link>
      <div className="myr-card__body">
        <span className="myr-card__brand">{product.brand || 'Moyurakkhi'}</span>
        <h5 className="myr-card__name">{product.name}</h5>
        <div className="myr-card__footer">
          <span className="myr-card__price">{product.price}৳</span>
          <button
            className={`myr-card__cart-btn${justAdded ? ' added' : ''}`}
            onClick={e => onAddToCart(product, e)}
            aria-label="Add to cart"
          >
            {justAdded
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

export default Home;